import { getBreakEndTime } from '@/lib/scheduledBreak/scheduledBreakFunctions';
import { withI18nMessage } from '@/plugins/vuelidate';
import { ShiftFormV2Type } from '@/types/shiftFormV2';
import {
  ScheduledBreakFormItemType,
  ShiftAreaSessionFormItemType,
} from '@/types/shiftSessionsForm';
import spacetime from 'spacetime';
import { ShiftArea } from '../../../../api/v1';
import { sessionsNoOverlap } from './reusableHelpers';

export const breakInsideShiftTimeRange = withI18nMessage(
  (getShiftStartTime: () => string, getShiftEndTime: () => string) =>
    (value: string, form: ScheduledBreakFormItemType) => {
      if (!value) {
        return true;
      }

      const shiftStartTime = getShiftStartTime();
      const shiftEndTime = getShiftEndTime();

      let breakStart = spacetime.now().time(value);
      let breakEnd = spacetime
        .now()
        .time(value)
        .add(form.durationInMinutes, 'minute');

      const shiftStart = spacetime.now().time(shiftStartTime);
      let shiftEnd = spacetime.now().time(shiftEndTime);
      if (shiftStart.epoch >= shiftEnd.epoch) {
        shiftEnd = shiftEnd.add(1, 'day');
        if (breakStart.isBefore(shiftStart)) {
          [breakStart, breakEnd] = [breakStart, breakEnd].map((time) =>
            time.add(1, 'day'),
          );
        }
      }

      return (
        breakStart.toNativeDate() >= shiftStart.toNativeDate() &&
        breakEnd.toNativeDate() <= shiftEnd.toNativeDate()
      );
    },
  { withArguments: true },
);

export const dateInsideTimeRange = withI18nMessage(
  (getStartTime: () => Date, getEndTime: () => Date) => (value: Date | null) => {
    const shiftStartTime = getStartTime();
    const shiftEndTime = getEndTime();

    if (!value || !shiftStartTime || !shiftEndTime) {
      return true;
    }

    return value >= shiftStartTime && value <= shiftEndTime;
  },
  { withArguments: true },
);

export const shiftSessionsInsideScheduledTimes = withI18nMessage(
  (timezone: string) => (_, form: ShiftFormV2Type) => {
    const breaks = form.scheduledBreaks.filter((b) => b.startsAt);
    const areaSessions = form.shiftAreaSessions;
    if (
      ![...breaks, ...areaSessions].length ||
      !form.startsAt ||
      !form.endsAt
    ) {
      return true;
    }

    const mappedSessions = [
      ...breaks.map((b) => ({
        start: b.startsAt,
        end: getBreakEndTime(b, timezone),
      })),
      ...form.shiftAreaSessions.map((s) => ({
        start: s.startTime,
        end: s.endTime,
      })),
    ];

    return mappedSessions.every((session) => {
      return session.start >= form.startsAt && session.end <= form.endsAt;
    });
  },
  { withArguments: true },
);

export const shiftSessionsNoOverlap = withI18nMessage(
  (timezone: string) => (_, form: ShiftFormV2Type) => {
    const breaks = form.scheduledBreaks.filter((b) => b.startsAt);

    if (!breaks.length && !form.shiftAreaSessions.length) {
      return true;
    }

    const mappedSessions = [
      ...breaks.map((b) => ({
        start: b.startsAt,
        end: getBreakEndTime(b, timezone),
      })),
      ...form.shiftAreaSessions.map((s) => ({
        start: s.startTime,
        end: s.endTime,
      })),
    ];

    return sessionsNoOverlap(mappedSessions);
  },
  { withArguments: true },
);

export const scheduledBreaksNoOverlap = withI18nMessage(
  (getShiftStartTime: () => string) => (value: ScheduledBreakFormItemType[]) => {
    const breaks = value.filter((b) => b.startTime);
    if (!breaks.length) {
      return true;
    }

    const shiftStart = spacetime.now().time(getShiftStartTime());

    const preparedBreaks = breaks.map((b) => {
      let start = spacetime.now().time(b.startTime);
      if (shiftStart.isAfter(start)) {
        start = start.add(1, 'day');
      }
      const end = spacetime(start)
        .add(b.durationInMinutes, 'minute')
        .toNativeDate();
      return { start: start.toNativeDate(), end };
    });

    return sessionsNoOverlap(preparedBreaks);
  },
  { withArguments: true },
);

export const areaSessionsNoOverlap = withI18nMessage(
  (sessions: ShiftAreaSessionFormItemType[]) => {
    if (!sessions.length) {
      return true;
    }
    return sessionsNoOverlap(
      sessions.map((s) => ({ start: s.startTime, end: s.endTime })),
    );
  },
);

export const scheduledBreaksAcceptableDuration = withI18nMessage(
  (getShiftStartTime: () => string, getShiftEndTime: () => string) =>
    (breaks: ScheduledBreakFormItemType[]) => {
      const shiftStartTime = getShiftStartTime();
      const shiftEndTime = getShiftEndTime();

      if (!shiftStartTime || !shiftEndTime || !breaks.length) {
        return true;
      }

      const start = spacetime.now().time(shiftStartTime);
      let end = spacetime.now().time(shiftEndTime);
      if (start.epoch >= end.epoch) {
        end = spacetime(end).add(1, 'day');
      }

      const breakSum = breaks.reduce(
        (acc, next) => acc + next.durationInMinutes,
        0,
      );

      return Math.abs(end.diff(start, 'minute')) >= breakSum;
    },
  { withArguments: true },
);

export const areaSessionsAcceptableDuration = withI18nMessage(
  (getShiftStartTime: () => string, getShiftEndTime: () => string) =>
    (sessions: ShiftAreaSessionFormItemType[]) => {
      const shiftStartTime = getShiftStartTime();
      const shiftEndTime = getShiftEndTime();

      if (!shiftStartTime || !shiftEndTime || !sessions.length) {
        return true;
      }

      const start = spacetime.now().time(shiftStartTime);
      let end = spacetime.now().time(shiftEndTime);
      if (start.epoch >= end.epoch) {
        end = spacetime(end).add(1, 'day');
      }

      const sessionsDuration = sessions.reduce(
        (acc, next) =>
          acc +
          spacetime(next.startTime).diff(spacetime(next.endTime), 'minute'),
        0,
      );

      return Math.abs(end.diff(start, 'minute')) >= sessionsDuration;
    },
  { withArguments: true },
);

export const shiftAreasAtLocation = withI18nMessage(
  (shiftAreas: ShiftArea[]) => (_, form: ShiftFormV2Type) => {
    const { locationId } = form;
    if (!locationId || !form.shiftAreaSessions.length) {
      return true;
    }
    return form.shiftAreaSessions.every((session) => {
      const area = shiftAreas.find((a) => a.id === session.shiftAreaId);
      return area.locationIds.includes(locationId);
    });
  },
  { withArguments: true },
);
